import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  DialogBody,
  Button,
  Input,
  Spinner,
  Dropdown,
  Option,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  MailRegular,
  PersonRegular,
  WarningRegular,
  CheckmarkCircleRegular,
  PenRegular,
  SearchRegular,
  PeopleRegular,
} from '@fluentui/react-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  validateForSignature,
  sendForSignature,
  getAppointmentAttendees,
  searchDataverse,
} from '../services/api';
import type {
  TaxReturn,
  TaxDocument,
  Form8879Type,
  SendForSignatureRequest,
  SignatureValidationResponse,
  ContactInfo,
} from '../types';
import {
  Form8879TypeDisplayNames,
  Form8879TypeDescriptions,
  requiresRemoteSignature,
  businessEntityTypeToFormType,
  signatureDocumentTypes,
} from '../types';

interface SendSignatureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taxReturn: TaxReturn;
  documents: TaxDocument[];
  onSuccess?: () => void;
}

export default function SendSignatureDialog({
  open,
  onOpenChange,
  taxReturn,
  documents,
  onSuccess,
}: SendSignatureDialogProps) {
  const queryClient = useQueryClient();

  // Form state
  const [selectedDocumentBlobName, setSelectedDocumentBlobName] = useState<string>('');
  const [formType, setFormType] = useState<Form8879Type>('8879');
  const [clientEmail, setClientEmail] = useState('');
  const [spouseEmail, setSpouseEmail] = useState('');
  const [authorizedSignerName, setAuthorizedSignerName] = useState('');
  const [authorizedSignerEmail, setAuthorizedSignerEmail] = useState('');

  // Validation state
  const [validationResult, setValidationResult] = useState<SignatureValidationResponse | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Signer picker state (business forms)
  const [suggestedContacts, setSuggestedContacts] = useState<{ name: string; email: string; source: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ContactInfo[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Only documents from the Signature Documents section can be sent for e-signature.
  const availableDocuments = documents.filter((d) => signatureDocumentTypes.includes(d.documentType));

  // Build suggested contacts from appointment attendees + tax return fields
  const loadSuggestedContacts = useCallback(async () => {
    const contacts: { name: string; email: string; source: string }[] = [];
    const seen = new Set<string>();

    const addContact = (name: string, email: string | undefined | null, source: string) => {
      if (!name || !email) return;
      const key = email.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      contacts.push({ name, email, source });
    };

    // Add business owner from tax return
    if (taxReturn.businessOwnerName) {
      addContact(taxReturn.businessOwnerName, taxReturn.clientEmail, 'Business Owner');
    }

    // Add previously used authorized signer
    if (taxReturn.authorizedSignerName && taxReturn.authorizedSignerEmail) {
      addContact(taxReturn.authorizedSignerName, taxReturn.authorizedSignerEmail, 'Previous Signer');
    }

    // Load appointment attendees
    if (taxReturn.appointmentId) {
      setLoadingSuggestions(true);
      try {
        const response = await getAppointmentAttendees(taxReturn.appointmentId);
        for (const attendee of response.attendees) {
          if (attendee.partyType === 'contact' && attendee.email) {
            addContact(attendee.partyName, attendee.email, 'Appointment');
          }
        }
      } catch {
        // Silently fail - suggestions are optional
      } finally {
        setLoadingSuggestions(false);
      }
    }

    setSuggestedContacts(contacts);
  }, [taxReturn]);

  // Initialize form values from tax return
  useEffect(() => {
    if (open && taxReturn) {
      setClientEmail(taxReturn.clientEmail || '');
      setSpouseEmail(taxReturn.spouseEmail || '');
      setAuthorizedSignerName(taxReturn.authorizedSignerName || '');
      setAuthorizedSignerEmail(taxReturn.authorizedSignerEmail || '');
      setSearchQuery('');
      setSearchResults([]);

      // Set default form type based on return type
      if (taxReturn.returnType === 'business') {
        if (taxReturn.businessEntityType && businessEntityTypeToFormType[taxReturn.businessEntityType]) {
          setFormType(businessEntityTypeToFormType[taxReturn.businessEntityType]);
        } else {
          setFormType('8879-C');
        }
        // Load suggested contacts for business forms
        loadSuggestedContacts();
      } else {
        setFormType('8879');
      }

      // Reset validation
      setValidationResult(null);
      setValidationError(null);
    }
  }, [open, taxReturn, loadSuggestedContacts]);

  // Debounced Dataverse search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await searchDataverse(searchQuery, 'contact', 10);
        setSearchResults(results.contacts);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectSigner = (name: string, email: string) => {
    setAuthorizedSignerName(name);
    setAuthorizedSignerEmail(email);
    setSearchQuery('');
    setSearchResults([]);
  };

  // Validate when form type changes or dialog opens
  const handleValidate = async () => {
    if (!taxReturn?.id) return;

    setIsValidating(true);
    setValidationError(null);

    try {
      const result = await validateForSignature(taxReturn.id, formType);
      setValidationResult(result);
    } catch (error) {
      setValidationError(error instanceof Error ? error.message : 'Validation failed');
      setValidationResult(null);
    } finally {
      setIsValidating(false);
    }
  };

  // Validate when form type changes
  useEffect(() => {
    if (open && taxReturn?.id) {
      handleValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, open, taxReturn?.id]);

  const sendMutation = useMutation({
    mutationFn: async (request: SendForSignatureRequest) => {
      return sendForSignature(taxReturn.id, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', taxReturn.id] });
      queryClient.invalidateQueries({ queryKey: ['taxDocuments', taxReturn.id] });
      onOpenChange(false);
      onSuccess?.();
      resetForm();
    },
  });

  const resetForm = () => {
    setSelectedDocumentBlobName('');
    setFormType('8879');
    setClientEmail('');
    setSpouseEmail('');
    setAuthorizedSignerName('');
    setAuthorizedSignerEmail('');
    setValidationResult(null);
    setValidationError(null);
    setSearchQuery('');
    setSearchResults([]);
    setSuggestedContacts([]);
  };

  const handleSend = () => {
    const request: SendForSignatureRequest = {
      documentBlobName: selectedDocumentBlobName,
      formType,
    };

    // Add appropriate signer info based on form type
    if (isBusinessForm) {
      request.authorizedSignerName = authorizedSignerName;
      request.authorizedSignerEmail = authorizedSignerEmail;
    } else {
      request.clientEmail = clientEmail;
      if (taxReturn.filingType === 'joint' && spouseEmail) {
        request.spouseEmail = spouseEmail;
      }
    }

    sendMutation.mutate(request);
  };

  const isBusinessForm = formType === '8879-C' || formType === '8879-S' || formType === '8879-PE';
  const isJointFiling = taxReturn.filingType === 'joint';

  // Determine if the form is valid
  const canSend = () => {
    if (!selectedDocumentBlobName) return false;
    if (isBusinessForm) {
      return authorizedSignerName.trim() && authorizedSignerEmail.trim();
    } else {
      if (!clientEmail.trim()) return false;
      if (isJointFiling && !spouseEmail.trim()) return false;
      return true;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        onOpenChange(data.open);
        if (!data.open) resetForm();
      }}
    >
      <DialogSurface style={{ maxWidth: '550px' }}>
        <DialogBody>
          <DialogTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PenRegular />
              Send for E-Signature
            </div>
          </DialogTitle>
          <DialogContent>
            {/* In-person appointment warning */}
            {!requiresRemoteSignature(taxReturn.appointmentType) && (
              <MessageBar intent="warning" style={{ marginBottom: '16px' }}>
                <MessageBarBody>
                  <MessageBarTitle>In-Person Appointment</MessageBarTitle>
                  This is an <strong>in-person</strong> appointment. Documents are typically signed on-site.
                  Are you sure you want to send for electronic signature via Zoho Sign?
                </MessageBarBody>
              </MessageBar>
            )}

            <div style={{ marginBottom: '16px' }}>
              <p style={{ color: 'var(--tl-text-secondary)', margin: 0 }}>
                Send the 8879 form to <strong>{taxReturn.clientName}</strong>
                {isJointFiling && taxReturn.spouseName && (
                  <> and <strong>{taxReturn.spouseName}</strong></>
                )}
                {' '}for electronic signature via Zoho Sign.
              </p>
            </div>

            {/* Validation Status */}
            {isValidating && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                <Spinner size="tiny" />
                <span>Validating signer information...</span>
              </div>
            )}

            {validationError && (
              <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                <MessageBarBody>
                  <MessageBarTitle>Validation Error</MessageBarTitle>
                  {validationError}
                </MessageBarBody>
              </MessageBar>
            )}

            {validationResult && !validationResult.isValid && (
              <MessageBar intent="warning" style={{ marginBottom: '16px' }}>
                <MessageBarBody>
                  <MessageBarTitle>Missing Information</MessageBarTitle>
                  <div style={{ marginTop: '8px' }}>
                    {(validationResult.missingFields ?? []).map((field) => (
                      <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                        <WarningRegular style={{ color: '#f59e0b', fontSize: '14px' }} />
                        <span>{(validationResult.fieldDescriptions ?? {})[field] || field}</span>
                      </div>
                    ))}
                  </div>
                  <p style={{ marginTop: '8px', marginBottom: 0, fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                    Please fill in the missing information below.
                  </p>
                </MessageBarBody>
              </MessageBar>
            )}

            {validationResult && validationResult.isValid && (
              <MessageBar intent="success" style={{ marginBottom: '16px' }}>
                <MessageBarBody>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckmarkCircleRegular />
                    <span>All required signer information is available</span>
                  </div>
                </MessageBarBody>
              </MessageBar>
            )}

            {/* Form Type Selection */}
            <Field label="Form Type" required style={{ marginBottom: '16px' }}>
              <Dropdown
                value={Form8879TypeDisplayNames[formType]}
                selectedOptions={[formType]}
                onOptionSelect={(_, data) => setFormType(data.optionValue as Form8879Type)}
                style={{ width: '100%' }}
              >
                {(Object.keys(Form8879TypeDisplayNames) as Form8879Type[]).map((type) => (
                  <Option key={type} value={type} text={Form8879TypeDisplayNames[type]}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{Form8879TypeDisplayNames[type]}</div>
                      <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                        {Form8879TypeDescriptions[type]}
                      </div>
                    </div>
                  </Option>
                ))}
              </Dropdown>
            </Field>

            {/* Document Selection */}
            <Field label="Document to Sign" required style={{ marginBottom: '16px' }}>
              {availableDocuments.length > 0 ? (
                <Dropdown
                  value={availableDocuments.find(d => d.blobName === selectedDocumentBlobName)?.fileName || ''}
                  selectedOptions={selectedDocumentBlobName ? [selectedDocumentBlobName] : []}
                  onOptionSelect={(_, data) => setSelectedDocumentBlobName(data.optionValue || '')}
                  style={{ width: '100%' }}
                  placeholder="Select a document..."
                >
                  {availableDocuments.map((doc) => (
                    <Option key={doc.blobName} value={doc.blobName} text={doc.fileName}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DocumentRegular />
                        <div>
                          <div style={{ fontWeight: 500 }}>{doc.fileName}</div>
                          <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>
                            Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </Option>
                  ))}
                </Dropdown>
              ) : (
                <div style={{ padding: '12px', backgroundColor: '#fff4ce', borderRadius: '4px', border: '1px solid #f59e0b' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WarningRegular style={{ color: '#f59e0b' }} />
                    <span>No documents available. Please upload the 8879 form first.</span>
                  </div>
                </div>
              )}
            </Field>

            {/* Signer Information */}
            <div style={{ borderTop: '1px solid var(--tl-border)', paddingTop: '16px', marginTop: '8px' }}>
              <h4 style={{ margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <PersonRegular />
                Signer Information
              </h4>

              {isBusinessForm ? (
                // Business Form - Authorized Signer Picker
                <>
                  {/* Suggested Contacts */}
                  {(suggestedContacts.length > 0 || loadingSuggestions) && (
                    <div style={{ marginBottom: '16px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#242424', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <PeopleRegular style={{ fontSize: '14px' }} />
                        Suggested Signers
                      </div>
                      {loadingSuggestions ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                          <Spinner size="tiny" />
                          Loading contacts...
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {suggestedContacts.map((contact) => (
                            <div
                              key={contact.email}
                              onClick={() => handleSelectSigner(contact.name, contact.email)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '4px',
                                border: authorizedSignerEmail.toLowerCase() === contact.email.toLowerCase()
                                  ? '1px solid #0078d4'
                                  : '1px solid var(--tl-border)',
                                backgroundColor: authorizedSignerEmail.toLowerCase() === contact.email.toLowerCase()
                                  ? '#eff6fc'
                                  : 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                              onMouseOver={(e) => {
                                if (authorizedSignerEmail.toLowerCase() !== contact.email.toLowerCase()) {
                                  e.currentTarget.style.backgroundColor = 'var(--tl-bg-subtle)';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (authorizedSignerEmail.toLowerCase() !== contact.email.toLowerCase()) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 500, fontSize: '13px' }}>{contact.name}</div>
                                <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>{contact.email}</div>
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--tl-text-tertiary)', backgroundColor: 'var(--tl-bg-subtle)', padding: '2px 8px', borderRadius: '10px' }}>
                                {contact.source}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Dataverse Search */}
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#242424', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <SearchRegular style={{ fontSize: '14px' }} />
                      Search Contacts
                    </div>
                    <Input
                      value={searchQuery}
                      onChange={(_, data) => setSearchQuery(data.value)}
                      placeholder="Search by name or email..."
                      contentBefore={<SearchRegular />}
                      contentAfter={isSearching ? <Spinner size="tiny" /> : undefined}
                      style={{ width: '100%' }}
                    />
                    {searchResults.length > 0 && (
                      <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid var(--tl-border)', borderRadius: '4px', marginTop: '4px', backgroundColor: 'white' }}>
                        {searchResults.map((contact) => (
                          <div
                            key={contact.contactId}
                            onClick={() => handleSelectSigner(contact.fullName, contact.email || '')}
                            style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--tl-bg-subtle)' }}
                            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = 'var(--tl-bg-subtle)')}
                            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <div style={{ fontWeight: 500, fontSize: '13px' }}>{contact.fullName}</div>
                            {contact.email && (
                              <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)' }}>{contact.email}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {searchQuery.trim().length >= 2 && !isSearching && searchResults.length === 0 && (
                      <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--tl-text-secondary)', marginTop: '4px' }}>
                        No contacts found
                      </div>
                    )}
                  </div>

                  {/* Manual entry / selected signer fields */}
                  <Field label="Authorized Signer Name" required style={{ marginBottom: '16px' }}>
                    <Input
                      value={authorizedSignerName}
                      onChange={(_, data) => setAuthorizedSignerName(data.value)}
                      placeholder="Enter authorized signer name"
                    />
                  </Field>
                  <Field label="Authorized Signer Email" required style={{ marginBottom: '16px' }}>
                    <Input
                      type="email"
                      value={authorizedSignerEmail}
                      onChange={(_, data) => setAuthorizedSignerEmail(data.value)}
                      placeholder="Enter authorized signer email"
                      contentBefore={<MailRegular />}
                    />
                  </Field>
                </>
              ) : (
                // Individual Form
                <>
                  <Field
                    label={isJointFiling ? `Taxpayer Email (${taxReturn.clientName})` : 'Taxpayer Email'}
                    required
                    style={{ marginBottom: '16px' }}
                  >
                    <Input
                      type="email"
                      value={clientEmail}
                      onChange={(_, data) => setClientEmail(data.value)}
                      placeholder="Enter taxpayer email"
                      contentBefore={<MailRegular />}
                    />
                  </Field>
                  {isJointFiling && (
                    <>
                      <Field
                        label={`Spouse Email (${taxReturn.spouseName || 'Spouse'})`}
                        required
                        style={{ marginBottom: '16px' }}
                      >
                        <Input
                          type="email"
                          value={spouseEmail}
                          onChange={(_, data) => setSpouseEmail(data.value)}
                          placeholder="Enter spouse email"
                          contentBefore={<MailRegular />}
                        />
                      </Field>
                      {!spouseEmail && (
                        <div style={{ marginTop: '-12px', marginBottom: '16px', padding: '8px 12px', backgroundColor: '#fff4ce', borderRadius: '4px', border: '1px solid #f59e0b' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                            <WarningRegular style={{ color: '#f59e0b', fontSize: '14px' }} />
                            <span>Spouse email is required for joint returns. Add it in the Overview tab or enter it above.</span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Summary */}
            <div style={{ backgroundColor: 'var(--tl-bg-subtle)', padding: '12px', borderRadius: '4px', marginTop: '16px' }}>
              <div style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                <strong>Summary:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Form: {Form8879TypeDisplayNames[formType]}</li>
                  {isBusinessForm ? (
                    <li>Signer: {authorizedSignerName || '(not set)'} &lt;{authorizedSignerEmail || '(not set)'}&gt;</li>
                  ) : (
                    <>
                      <li>Taxpayer: {taxReturn.clientName} &lt;{clientEmail || '(not set)'}&gt;</li>
                      {isJointFiling && (
                        <li>Spouse: {taxReturn.spouseName} &lt;{spouseEmail || '(not set)'}&gt;</li>
                      )}
                    </>
                  )}
                </ul>
              </div>
            </div>

            {/* Error display */}
            {sendMutation.isError && (
              <MessageBar intent="error" style={{ marginTop: '16px' }}>
                <MessageBarBody>
                  <MessageBarTitle>Error</MessageBarTitle>
                  {sendMutation.error?.message || 'Failed to send for signature'}
                </MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              icon={<PenRegular />}
              onClick={handleSend}
              disabled={sendMutation.isPending || !canSend() || availableDocuments.length === 0}
            >
              {sendMutation.isPending ? (
                <>
                  <Spinner size="tiny" style={{ marginRight: '8px' }} />
                  Sending...
                </>
              ) : (
                'Send for Signature'
              )}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
